import React from 'react';
import { formatDateKey } from './utils';
import { monthNames, dayNames } from './constants';
import { CalendarDay, HolidayApiResponse, HolidayMap } from '../../types';

interface CalendarViewProps {
  currentDate: Date;
  onDateSelect: (date: Date) => void;
  selectedDate: Date | null;
  holidays: HolidayMap; // ✅ changed from HolidayApiResponse
}

const CalendarView: React.FC<CalendarViewProps> = ({ 
  currentDate, 
  onDateSelect, 
  selectedDate, 
  holidays 
}) => {
  const month = currentDate.getMonth();
  const year = currentDate.getFullYear();
  
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDayOfMonth = new Date(year, month, 1).getDay();
  
  const days: CalendarDay[] = [];
  
  // Previous month's trailing days
  for (let i = firstDayOfMonth - 1; i >= 0; i--) {
    const prevMonthDate = new Date(year, month, -i);
    days.push({
      date: prevMonthDate.getDate(),
      isCurrentMonth: false,
      fullDate: prevMonthDate
    });
  }
  
  // Current month's days
  for (let day = 1; day <= daysInMonth; day++) {
    const fullDate = new Date(year, month, day);
    days.push({
      date: day,
      isCurrentMonth: true,
      fullDate: fullDate
    });
  }
  
  // Next month's leading days
  const remainingDays = 42 - days.length;
  for (let day = 1; day <= remainingDays; day++) {
    const nextMonthDate = new Date(year, month + 1, day);
    days.push({
      date: day,
      isCurrentMonth: false,
      fullDate: nextMonthDate
    });
  }
  
  const getHolidayType = (date: Date): string | undefined => {
    const dateKey = formatDateKey(date);
    return holidays[dateKey]?.type;
  };
  
  const isSelected = (date: Date): boolean => {
    return selectedDate ? formatDateKey(date) === formatDateKey(selectedDate) : false;
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="flex items-center justify-center mb-4">
        <h3 className="text-lg font-medium text-gray-900">
          {monthNames[month]} {year}
        </h3>
      </div>
      
      <div className="grid grid-cols-7 gap-1 mb-2">
        {dayNames.map(day => (
          <div key={day} className="text-center text-sm font-medium text-gray-500 py-2">
            {day}
          </div>
        ))}
      </div>
      
      <div className="grid grid-cols-7 gap-1">
        {days.map((day, index) => {
          const holidayType = getHolidayType(day.fullDate);
          const selected = isSelected(day.fullDate);
          
          return (
            <button
              key={index}
              onClick={() => day.isCurrentMonth && onDateSelect(day.fullDate)}
              className={`
                aspect-square p-2 text-sm rounded-lg transition-colors relative
                ${day.isCurrentMonth 
                  ? 'hover:bg-blue-50 text-gray-900' 
                  : 'text-gray-400 cursor-not-allowed'
                }
                ${selected ? 'bg-blue-100 border-2 border-blue-500' : ''}
                ${holidayType === 'federal' ? 'bg-red-100 border border-red-300' : ''}
                ${holidayType === 'company' ? 'bg-green-100 border border-green-300' : ''}
                ${holidayType === 'observed' ? 'bg-orange-100 border border-orange-300' : ''}
              `}
              disabled={!day.isCurrentMonth}
            >
              {day.date}
              {holidayType && (
                <div className={`
                  absolute bottom-1 right-1 w-2 h-2 rounded-full
                  ${holidayType === 'federal' ? 'bg-red-500' : ''}
                  ${holidayType === 'company' ? 'bg-green-500' : ''}
                  ${holidayType === 'observed' ? 'bg-orange-500' : ''}
                `} />
              )}
            </button>
          );
        })}
      </div>
      
      <div className="flex justify-center gap-4 mt-4">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-red-500 rounded-full"></div>
          <span className="text-xs text-gray-600">Federal Holiday</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-green-500 rounded-full"></div>
          <span className="text-xs text-gray-600">Company Holiday</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
          <span className="text-xs text-gray-600">Observed Holiday</span>
        </div>
      </div>
    </div>
  );
};

export default CalendarView;